import React, { useState } from 'react';
import { AppShell } from '../components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { authApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

export default function AdminSettings() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password) {
      toast.error('Current password is required');
      return;
    }
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        current_password: form.current_password,
      };
      if (form.email !== user?.email) {
        payload.email = form.email;
      }
      if (form.new_password) {
        payload.new_password = form.new_password;
      }
      await authApi.updateSettings(payload);
      toast.success('Settings updated. Please log in again if you changed your email.');
      setForm((p) => ({ ...p, current_password: '', new_password: '', confirm_password: '' }));
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Admin Settings' },
      ]}
    >
      <div className="p-4 sm:p-6 lg:p-8 max-w-xl">
        <h1 className="text-xl md:text-2xl font-semibold font-display tracking-tight mb-6">Admin Settings</h1>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display">Account Credentials</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Current Password *</Label>
                <Input
                  type="password"
                  value={form.current_password}
                  onChange={(e) => setForm((p) => ({ ...p, current_password: e.target.value }))}
                  placeholder="Enter current password"
                  data-testid="admin-current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={form.new_password}
                  onChange={(e) => setForm((p) => ({ ...p, new_password: e.target.value }))}
                  placeholder="Leave blank to keep current"
                  data-testid="admin-new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => setForm((p) => ({ ...p, confirm_password: e.target.value }))}
                  placeholder="Confirm new password"
                />
              </div>
              <Button type="submit" disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
